export const SCOPE_OF_ADVICE_TOKEN = '{{deal.scopeOfAdviceTable}}';

const getDefaultHtmlContentValues = () => ({
	displayCondition: null,
	_styleGuide: null,
	containerPadding: '10px',
	anchor: '',
	_meta: {
		htmlClassNames: 'u_content_html',
	},
	selectable: true,
	draggable: true,
	duplicatable: true,
	deletable: true,
	hideable: true,
	locked: false,
});

const cloneJsonSafe = (value) => JSON.parse(JSON.stringify(value));

const findHtmlContentValuesTemplate = (design) => {
	const rows = design?.body?.rows;
	if (!Array.isArray(rows)) return null;

	for (const row of rows) {
		const columns = row?.columns;
		if (!Array.isArray(columns)) continue;
		for (const column of columns) {
			const contents = column?.contents;
			if (!Array.isArray(contents)) continue;
			for (const content of contents) {
				if (content?.type !== 'html' || typeof content?.values !== 'object') continue;
				return cloneJsonSafe(content.values);
			}
		}
	}

	return null;
};

const replaceTokenInString = (value, token, replacement) => {
	if (typeof value !== 'string') return value;
	if (!token || typeof token !== 'string') return value;
	const safeReplacement =
		replacement === undefined || replacement === null ? '' : String(replacement);

	return value.split(token).join(safeReplacement);
};

const contentContainsScopeOfAdviceToken = (content) =>
	countExactTokenInDesign(content, SCOPE_OF_ADVICE_TOKEN) > 0;

const buildHtmlContentBlock = ({
	baseContent,
	htmlValuesTemplate,
	html,
	htmlCounter,
	idSuffix = '',
}) => ({
	...(baseContent ?? {}),
	id:
		typeof baseContent?.id === 'string' && baseContent.id.length > 0
			? `${baseContent.id}${idSuffix}`
			: `u_content_html_${htmlCounter}${idSuffix}`,
	type: 'html',
	values: {
		...htmlValuesTemplate,
		html,
		_meta: {
			...(htmlValuesTemplate?._meta ?? {}),
			htmlID: `u_content_html_${htmlCounter}`,
			htmlClassNames: htmlValuesTemplate?._meta?.htmlClassNames ?? 'u_content_html',
		},
	},
});

const getInitialHtmlCounter = (design) =>
	Number.isFinite(Number(design?.counters?.u_content_html))
		? Number(design.counters.u_content_html)
		: 0;

const getValidHtmlBlocks = (htmlBlocks) =>
	Array.isArray(htmlBlocks)
		? htmlBlocks.filter((html) => typeof html === 'string' && html.length > 0)
		: [];

const buildScopeInjectionBlocks = ({
	baseContent,
	htmlValuesTemplate,
	htmlBlocks,
	htmlCounter,
}) => {
	const validHtmlBlocks = getValidHtmlBlocks(htmlBlocks);
	if (validHtmlBlocks.length === 0) {
		return {
			replacementBlocks: [],
			nextHtmlCounter: htmlCounter - 1,
			insertedAdditionalBlockCount: 0,
		};
	}

	let nextHtmlCounter = htmlCounter - 1;
	const replacementBlocks = validHtmlBlocks.map((html, index) => {
		nextHtmlCounter += 1;
		const isPrimaryBlock = index === 0;
		const idSuffix = isPrimaryBlock ? '' : `_additional_${index}`;
		return buildHtmlContentBlock({
			baseContent,
			htmlValuesTemplate,
			html,
			htmlCounter: nextHtmlCounter,
			idSuffix,
		});
	});

	return {
		replacementBlocks,
		nextHtmlCounter,
		insertedAdditionalBlockCount: Math.max(0, validHtmlBlocks.length - 1),
	};
};

const replaceScopeTokenInsideHtmlContent = (content, primaryHtmlBlock) => {
	if (content?.type !== 'html' || typeof content?.values?.html !== 'string') return content;
	return {
		...content,
		values: {
			...content.values,
			html: replaceTokenInString(
				content.values.html,
				SCOPE_OF_ADVICE_TOKEN,
				primaryHtmlBlock
			),
		},
	};
};

const mutateColumnContentsForScopeInjection = ({
	contents,
	htmlValuesTemplate,
	htmlBlocks,
	nextHtmlCounter,
}) => {
	let convertedTextBlockCount = 0;
	let insertedAdditionalBlockCount = 0;
	let updatedHtmlCounter = nextHtmlCounter;
	const primaryHtmlBlock = getValidHtmlBlocks(htmlBlocks)[0] ?? '';

	for (let index = 0; index < contents.length; index += 1) {
		const content = contents[index];
		if (!content || typeof content !== 'object') continue;

		if (contentContainsScopeOfAdviceToken(content)) {
			updatedHtmlCounter += 1;
			const {
				replacementBlocks,
				nextHtmlCounter: scopedHtmlCounter,
				insertedAdditionalBlockCount: scopedInsertedAdditionalBlockCount,
			} = buildScopeInjectionBlocks({
				baseContent: content,
				htmlValuesTemplate,
				htmlBlocks,
				htmlCounter: updatedHtmlCounter,
			});
			if (replacementBlocks.length === 0) {
				updatedHtmlCounter -= 1;
				continue;
			}
			updatedHtmlCounter = scopedHtmlCounter;
			convertedTextBlockCount += 1;
			insertedAdditionalBlockCount += scopedInsertedAdditionalBlockCount;
			contents.splice(index, 1, ...replacementBlocks);
			index += replacementBlocks.length - 1;
			continue;
		}

		contents[index] = replaceScopeTokenInsideHtmlContent(content, primaryHtmlBlock);
	}

	return {
		nextHtmlCounter: updatedHtmlCounter,
		convertedTextBlockCount,
		insertedAdditionalBlockCount,
	};
};

const mutateRowsForScopeInjection = ({
	rows,
	htmlValuesTemplate,
	htmlBlocks,
	nextHtmlCounter,
}) => {
	let convertedTextBlockCount = 0;
	let insertedAdditionalBlockCount = 0;
	let updatedHtmlCounter = nextHtmlCounter;

	for (const row of rows) {
		const columns = row?.columns;
		if (!Array.isArray(columns)) continue;

		for (const column of columns) {
			const contents = column?.contents;
			if (!Array.isArray(contents)) continue;

			const mutationResult = mutateColumnContentsForScopeInjection({
				contents,
				htmlValuesTemplate,
				htmlBlocks,
				nextHtmlCounter: updatedHtmlCounter,
			});
			updatedHtmlCounter = mutationResult.nextHtmlCounter;
			convertedTextBlockCount += mutationResult.convertedTextBlockCount;
			insertedAdditionalBlockCount += mutationResult.insertedAdditionalBlockCount;
		}
	}

	return {
		nextHtmlCounter: updatedHtmlCounter,
		convertedTextBlockCount,
		insertedAdditionalBlockCount,
	};
};

const tryInsertFallbackScopeBlocks = ({
	rows,
	htmlValuesTemplate,
	htmlBlocks,
	nextHtmlCounter,
}) => {
	const firstColumn = rows?.[0]?.columns?.[0];
	if (!Array.isArray(firstColumn?.contents)) {
		return {
			nextHtmlCounter,
			convertedTextBlockCount: 0,
			insertedAdditionalBlockCount: 0,
			usedFallbackInsertion: false,
		};
	}

	const baseContent = firstColumn.contents[0] ?? {};
	const validHtmlBlocks = getValidHtmlBlocks(htmlBlocks);
	if (validHtmlBlocks.length === 0) {
		return {
			nextHtmlCounter,
			convertedTextBlockCount: 0,
			insertedAdditionalBlockCount: 0,
			usedFallbackInsertion: false,
		};
	}

	let updatedHtmlCounter = nextHtmlCounter;
	validHtmlBlocks.forEach((html, index) => {
		updatedHtmlCounter += 1;
		firstColumn.contents.push(
			buildHtmlContentBlock({
				baseContent,
				htmlValuesTemplate,
				html,
				htmlCounter: updatedHtmlCounter,
				idSuffix:
					index === 0
						? '_fallback_scope_table'
						: `_fallback_additional_${index}`,
			})
		);
	});

	const insertedAdditionalBlockCount = Math.max(0, validHtmlBlocks.length - 1);

	return {
		nextHtmlCounter: updatedHtmlCounter,
		convertedTextBlockCount: 1,
		insertedAdditionalBlockCount,
		usedFallbackInsertion: true,
	};
};

export const applyTokenReplacementsToDesign = (value, tokenReplacements = {}) => {
	if (typeof value === 'string') {
		return Object.entries(tokenReplacements).reduce((nextValue, [token, replacement]) => {
			return replaceTokenInString(nextValue, token, replacement);
		}, value);
	}

	if (Array.isArray(value)) {
		return value.map((item) => applyTokenReplacementsToDesign(item, tokenReplacements));
	}

	if (value && typeof value === 'object') {
		return Object.entries(value).reduce((acc, [key, entryValue]) => {
			acc[key] = applyTokenReplacementsToDesign(entryValue, tokenReplacements);
			return acc;
		}, {});
	}

	return value;
};

export const injectScopeOfAdviceTableIntoDesign = (
	design,
	htmlBlocks = []
) => {
	if (!design || typeof design !== 'object') {
		return {
			design,
			convertedTextBlockCount: 0,
			insertedAdditionalBlockCount: 0,
			usedFallbackInsertion: false,
		};
	}
	const validHtmlBlocks = getValidHtmlBlocks(htmlBlocks);
	if (validHtmlBlocks.length === 0) {
		return {
			design,
			convertedTextBlockCount: 0,
			insertedAdditionalBlockCount: 0,
			usedFallbackInsertion: false,
		};
	}

	const nextDesign = cloneJsonSafe(design);
	const htmlValuesTemplate =
		findHtmlContentValuesTemplate(nextDesign) ?? getDefaultHtmlContentValues();
	let nextHtmlCounter = getInitialHtmlCounter(nextDesign);
	let convertedTextBlockCount = 0;
	let insertedAdditionalBlockCount = 0;
	let usedFallbackInsertion = false;

	const rows = nextDesign?.body?.rows;
	if (!Array.isArray(rows)) {
		return {
			design: nextDesign,
			convertedTextBlockCount,
			insertedAdditionalBlockCount,
			usedFallbackInsertion: false,
		};
	}

	const rowMutationResult = mutateRowsForScopeInjection({
		rows,
		htmlValuesTemplate,
		htmlBlocks: validHtmlBlocks,
		nextHtmlCounter,
	});
	nextHtmlCounter = rowMutationResult.nextHtmlCounter;
	convertedTextBlockCount += rowMutationResult.convertedTextBlockCount;
	insertedAdditionalBlockCount += rowMutationResult.insertedAdditionalBlockCount;

	if (convertedTextBlockCount === 0) {
		const fallbackInsertionResult = tryInsertFallbackScopeBlocks({
			rows,
			htmlValuesTemplate,
			htmlBlocks: validHtmlBlocks,
			nextHtmlCounter,
		});
		nextHtmlCounter = fallbackInsertionResult.nextHtmlCounter;
		convertedTextBlockCount += fallbackInsertionResult.convertedTextBlockCount;
		insertedAdditionalBlockCount += fallbackInsertionResult.insertedAdditionalBlockCount;
		usedFallbackInsertion = fallbackInsertionResult.usedFallbackInsertion;
	}

	if (convertedTextBlockCount > 0) {
		nextDesign.counters = {
			...(nextDesign?.counters ?? {}),
			u_content_html: nextHtmlCounter,
		};
	}

	return {
		design: nextDesign,
		convertedTextBlockCount,
		insertedAdditionalBlockCount,
		usedFallbackInsertion,
	};
};

export const countExactTokenInDesign = (value, token) => {
	if (!token || typeof token !== 'string') return 0;
	if (typeof value === 'string') {
		return value.split(token).length - 1;
	}
	if (Array.isArray(value)) {
		return value.reduce((acc, item) => acc + countExactTokenInDesign(item, token), 0);
	}
	if (value && typeof value === 'object') {
		return Object.values(value).reduce(
			(acc, entryValue) => acc + countExactTokenInDesign(entryValue, token),
			0
		);
	}
	return 0;
};

export const designContainsScopeOfAdviceToken = (value) =>
	countExactTokenInDesign(value, SCOPE_OF_ADVICE_TOKEN) > 0;
